"use client";

import { useUser } from "@clerk/nextjs";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { UserLanguagePreferences } from "@/lib/i18n/config";

type UpdateLanguagePreferencesInput = Partial<UserLanguagePreferences>;

type UserLanguagePreferencesContextValue = UserLanguagePreferences & {
  isSaving: boolean;
  updatePreferences: (
    patch: UpdateLanguagePreferencesInput
  ) => Promise<UserLanguagePreferences>;
};

const UserLanguagePreferencesContext =
  createContext<UserLanguagePreferencesContextValue | null>(null);

type UserLanguagePreferencesProviderProps = {
  children: React.ReactNode;
  initialPreferences: UserLanguagePreferences;
};

type PreferencesResponse =
  | {
      ok: true;
      preferences: UserLanguagePreferences;
    }
  | {
      ok: false;
      message?: string;
    };

const getPreferencesErrorMessage = (
  payload: PreferencesResponse | null
): string =>
  payload && !payload.ok && payload.message
    ? payload.message
    : "Unable to update preferences.";

export default function UserLanguagePreferencesProvider({
  children,
  initialPreferences,
}: UserLanguagePreferencesProviderProps) {
  const { isLoaded, user } = useUser();
  const [preferences, setPreferences] =
    useState<UserLanguagePreferences>(initialPreferences);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isLoaded || !user?.id) {
      return;
    }

    const controller = new AbortController();

    const loadPreferences = async () => {
      try {
        const response = await fetch("/api/user/preferences", {
          method: "GET",
          signal: controller.signal,
        });
        const payload = (await response.json().catch(() => null)) as
          | PreferencesResponse
          | null;

        if (!response.ok || !payload?.ok) {
          return;
        }

        setPreferences(payload.preferences);
      } catch {
        // Keep the initial client defaults if the request fails.
      }
    };

    void loadPreferences();

    return () => controller.abort();
  }, [isLoaded, user?.id]);

  const updatePreferences = useCallback(
    async (patch: UpdateLanguagePreferencesInput) => {
      const previousPreferences = preferences;
      const optimisticPreferences: UserLanguagePreferences = {
        appLocale: patch.appLocale ?? previousPreferences.appLocale,
        defaultOutputLocale:
          patch.defaultOutputLocale ?? previousPreferences.defaultOutputLocale,
      };

      setPreferences(optimisticPreferences);
      setIsSaving(true);

      try {
        const response = await fetch("/api/user/preferences", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patch),
        });
        const payload = (await response.json().catch(() => null)) as
          | PreferencesResponse
          | null;

        if (!response.ok || !payload?.ok) {
          throw new Error(getPreferencesErrorMessage(payload));
        }

        setPreferences(payload.preferences);

        return payload.preferences;
      } catch (error) {
        setPreferences(previousPreferences);
        throw error instanceof Error
          ? error
          : new Error("Unable to update preferences.");
      } finally {
        setIsSaving(false);
      }
    },
    [preferences]
  );

  const value = useMemo<UserLanguagePreferencesContextValue>(
    () => ({
      ...preferences,
      isSaving,
      updatePreferences,
    }),
    [isSaving, preferences, updatePreferences]
  );

  return (
    <UserLanguagePreferencesContext.Provider value={value}>
      {children}
    </UserLanguagePreferencesContext.Provider>
  );
}

export const useUserLanguagePreferences = () => {
  const context = useContext(UserLanguagePreferencesContext);

  if (!context) {
    throw new Error(
      "useUserLanguagePreferences must be used within UserLanguagePreferencesProvider."
    );
  }

  return context;
};
